import React from 'react';
import styled from 'styled-components';
import { 
  MapPin, 
  Mail, 
  Globe, 
  Calendar, 
  User, 
  Building2,
  FileText,
  Clock,
  Award,
  Scale,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import medPhoto from '../assets/MED1.jpeg';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  margin-top: -3rem;
  position: relative;
  z-index: 2;
  padding-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: -2rem;
  }
`;

// Carte d'identité
const IdentityCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  height: fit-content;
  border: 1px solid var(--border-light);
`;

const IdentityHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem;
  text-align: center;
  color: var(--white);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    font-weight: 700;
  }
`;

const MediatorPhoto = styled.div`
  width: 160px;
  height: 160px;
  margin: -2.5rem auto 1rem;
  border-radius: 50%;
  border: 5px solid var(--white);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  background: var(--background-light);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
  }
`;

const IdentityContent = styled.div`
  padding: 1.5rem;
`;

const IdentityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .label {
    font-size: 0.8rem;
    color: var(--text-light);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .value {
    font-size: 0.95rem;
    color: var(--text-dark);
    font-weight: 500;
    line-height: 1.4;

    a {
      color: var(--primary-blue);
      text-decoration: underline;

      &:hover {
        color: var(--secondary-red);
      }
    }
  }
`;

// Contenu principal
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContentCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  padding: 2rem;
  border: 1px solid var(--border-light);
`;

const CardTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-light);

  svg {
    color: var(--accent-gold);
  }
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 2.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, var(--accent-gold), var(--primary-blue));
    border-radius: 3px;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 2rem;

  &:last-child {
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: calc(-2rem - 6px);
    top: 0.35rem;
    width: 16px;
    height: 16px;
    background: var(--accent-gold);
    border-radius: 50%;
    border: 3px solid var(--white);
    box-shadow: 0 0 0 2px var(--accent-gold);
    z-index: 2;
  }

  &.highlight::before {
    background: var(--secondary-red);
    box-shadow: 0 0 0 2px var(--secondary-red);
  }
`

const TimelineDate = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--secondary-red);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimelineTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const TimelineText = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  line-height: 1.7;
`;

const PrerogativeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PrerogativeItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--background-light);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--accent-gold);

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 0.95rem;
    color: var(--text-dark);
    line-height: 1.5;
  }
`;

const MissionText = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(0, 38, 100, 0.05), rgba(254, 202, 0, 0.05));
  border-radius: var(--radius-md);
  border-left: 4px solid var(--primary-blue);
`;

const Historique = () => {
  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Historique</HeroTitle>
          <HeroSubtitle>
            Découvrez l'histoire et l'évolution de la Médiature de la République du Tchad
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentGrid>
          {/* Carte d'identité */}
          <IdentityCard>
            <IdentityHeader>
            </IdentityHeader>

            <MediatorPhoto>
              <img src={medPhoto} alt="Amb. Saleh KEBZABO" />
            </MediatorPhoto>

            <IdentityContent>
              <IdentityItem>
                <Building2 size={18} />
                <div>
                  <div className="label">Institution</div>
                  <div className="value">Médiature de la République du Tchad</div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <User size={18} />
                <div>
                  <div className="label">Médiateur</div>
                  <div className="value">Amb. Saleh KEBZABO</div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <Calendar size={18} />
                <div>
                  <div className="label">Mandat</div>
                  <div className="value">Depuis février 2024</div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <Award size={18} />
                <div>
                  <div className="label">Statut</div>
                  <div className="value">Membre actif AOMA et AOMF</div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <MapPin size={18} />
                <div>
                  <div className="label">Siège</div>
                  <div className="value">Avenue Colonel NELDE ROGOBERT, N'Djamena</div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <Mail size={18} />
                <div>
                  <div className="label">Email</div>
                  <div className="value">
                    <a href="mailto:contact@mediature.td">contact@mediature.td</a>
                  </div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <Globe size={18} />
                <div>
                  <div className="label">Site web</div>
                  <div className="value">
                    <a href="https://mediature.td" target="_blank" rel="noopener noreferrer">
                      mediature.td
                    </a>
                  </div>
                </div>
              </IdentityItem>

              <IdentityItem>
                <FileText size={18} />
                <div>
                  <div className="label">BP</div>
                  <div className="value">898</div>
                </div>
              </IdentityItem>
            </IdentityContent>
          </IdentityCard>

          {/* Contenu principal */}
          <MainContent>
            {/* Historique */}
            <ContentCard>
              <CardTitle>
                <Clock size={24} />
                Historique de l'institution
              </CardTitle>

              <Timeline>
                <TimelineItem>
                  <TimelineDate>
                    <Calendar size={14} />
                    12 août 1997
                  </TimelineDate>
                  <TimelineTitle>Création du poste de Médiateur national</TimelineTitle>
                  <TimelineText>
                    Le décret n° 340/PR/PM/97 crée au Tchad un poste de « Médiateur national », 
                    une institution chargée de contribuer au rétablissement et au maintien de la 
                    paix civile et politique dans le pays. Ce texte est un acte réglementaire 
                    instituant une fonction de médiation rattachée à la primature.
                  </TimelineText>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDate>
                    <Calendar size={14} />
                    1997 - 2007
                  </TimelineDate>
                  <TimelineTitle>Premier Médiateur national : Koibla Djimasta</TimelineTitle>
                  <TimelineText>
                    Koibla Djimasta occupe le poste de Médiateur national de 1997 jusqu'à son 
                    décès en 2007.
                  </TimelineText>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDate>
                    <Calendar size={14} />
                    11 décembre 2009
                  </TimelineDate>
                  <TimelineTitle>Institution de la Médiature de la République</TimelineTitle>
                  <TimelineText>
                    La Loi n°09-031 instaure un organe indépendant appelé Médiature de la République. 
                    Cette loi définit ses attributions, sa mission de médiation entre l'administration 
                    publique et les citoyens, et la nomination du Médiateur par décret présidentiel. 
                    Cependant, la Médiature d'origine n'était pas régie par une loi claire avant cette date, 
                    ce qui limitait ses attributions sur le long terme.
                  </TimelineText>
                </TimelineItem>

                <TimelineItem className="highlight">
                  <TimelineDate>
                    <Calendar size={14} />
                    2018
                  </TimelineDate>
                  <TimelineTitle>Suppression de l'institution</TimelineTitle>
                  <TimelineText>
                    La Médiature est supprimée à la suite du 1er Forum National Inclusif tenu à N'Djamena.
                  </TimelineText>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDate>
                    <Calendar size={14} />
                    29 décembre 2023
                  </TimelineDate>
                  <TimelineTitle>Restauration par la nouvelle Constitution</TimelineTitle>
                  <TimelineText>
                    La Médiature est restaurée conformément aux recommandations du Dialogue National 
                    Inclusif et Souverain (DNIS) de 2022, avec une définition renouvelée de son rôle 
                    et de son indépendance institutionnelle.
                  </TimelineText>
                </TimelineItem>

                <TimelineItem>
                  <TimelineDate>
                    <Calendar size={14} />
                    Février 2024
                  </TimelineDate>
                  <TimelineTitle>Nomination de l'Amb. Saleh KEBZABO</TimelineTitle>
                  <TimelineText>
                    Le premier Médiateur de la République nommé dans le cadre de la restauration 
                    de l'institution est l'Amb. Saleh KEBZABO, ancien Premier Ministre et haut cadre politique.
                  </TimelineText>
                </TimelineItem>
              </Timeline>
            </ContentCard>

            {/* Missions */}
            <ContentCard>
              <CardTitle>
                <Scale size={24} />
                Missions générales
              </CardTitle>
              <MissionText>
                La Médiature de la République est une <strong>autorité administrative indépendante</strong>, 
                investie d'une mission de service public de médiation. Elle est conçue comme un 
                <strong> intercesseur entre l'administration publique et les administrés</strong>, chargé 
                de recevoir et examiner les réclamations liées au fonctionnement des services publics 
                et de proposer des solutions amiables pour résoudre les litiges.
              </MissionText>
            </ContentCard>

            {/* Prérogatives */}
            <ContentCard>
              <CardTitle>
                <CheckCircle2 size={24} />
                Prérogatives
              </CardTitle>

              <PrerogativeGrid>
                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Apporter une assistance aux citoyens pour faire valoir leurs droits</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Instruire les réclamations liées aux dysfonctionnements des services publics</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Faire des suggestions pour un règlement à l'amiable des litiges</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Proposer au Président de la République des modifications législatives</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Participer à l'amélioration des services publics</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Contribuer à la réconciliation nationale et à la préservation de la paix</span>
                </PrerogativeItem>

                <PrerogativeItem>
                  <ChevronRight size={20} />
                  <span>Renforcer la cohésion sociale</span>
                </PrerogativeItem>
              </PrerogativeGrid>
            </ContentCard>
          </MainContent>
        </ContentGrid>
      </Container>
    </PageContainer>
  );
};

export default Historique;
